import apiService from '../Api'
import {
  ISupportTicket,
  ISupportTicketComplete,
  ISupportTicketCreatePayload,
  ISupportTicketUpdatePayload
} from './interfaces'

export class SupportService {
  async list(filter: {
    search: string
    status: string
    module: string
    user: string
    page: number
    page_size: number
  }): Promise<{
    message: string
    supportTickets: ISupportTicket[]
    current_page: number
    page_size: number
    total_filtered: number
  }> {
    return apiService.get('/support-tickets', { params: filter }).then((response) => response.data)
  }

  async show(id: number): Promise<{
    message: string
    supportTicket: ISupportTicketComplete
  }> {
    return apiService.get(`/support-tickets/${id}`).then((response) => response.data)
  }

  async create(payload: ISupportTicketCreatePayload): Promise<{
    message: string
    supportTicket: ISupportTicket
  }> {
    const formData = new FormData()

    formData.append('resume', payload.resume)
    formData.append('phone', payload.phone)
    formData.append('module', payload.module)
    formData.append('description', payload.description)

    payload.attachments?.forEach((file) => {
      formData.append('attachments[]', file)
    })

    return apiService
      .post('/support-tickets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async update(
    id: number,
    payload: ISupportTicketUpdatePayload
  ): Promise<{
    message: string
    supportTicket: ISupportTicket
  }> {
    const formData = new FormData()

    formData.append('_method', 'PUT')
    if (payload.resume) formData.append('resume', payload.resume)
    if (payload.phone) formData.append('phone', payload.phone)
    if (payload.module) formData.append('module', payload.module)
    if (payload.status) formData.append('status', payload.status)
    if (payload.description) formData.append('description', payload.description)
    if (payload.resolution_message) formData.append('resolution_message', payload.resolution_message)

    payload.attachments?.forEach((file) => {
      formData.append('attachments[]', file)
    })

    // Note: The API expects a POST request with _method=PUT for updates when using multipart/form-data
    return apiService
      .post(`/support-tickets/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async destroy(id: number): Promise<{
    message: string
  }> {
    return apiService.delete(`/support-tickets/${id}`).then((response) => response.data)
  }

  async createComment(
    ticketId: number,
    message: string,
    attachments?: File[],
    switch_assignee?: boolean
  ): Promise<{
    message: string
    comment: string
  }> {
    const formData = new FormData()
    formData.append('message', message)
    attachments?.forEach((file) => {
      formData.append('attachments[]', file)
    })

    if (switch_assignee) {
      formData.append('switch_assignee', '1')
    }

    return apiService
      .post(`/support-tickets/${ticketId}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((response) => response.data)
  }

  async getRecentTickets(): Promise<{
    message: string
    supportTickets: ISupportTicket[]
  }> {
    return apiService.get('/support-tickets/recent').then((response) => response.data)
  }

  async watchTicket(id: number): Promise<{
    message: string
  }> {
    return apiService.post(`/support-tickets/${id}/watch`).then((response) => response.data)
  }

  async unwatchTicket(id: number): Promise<{
    message: string
  }> {
    return apiService.post(`/support-tickets/${id}/unwatch`).then((response) => response.data)
  }

  async listTicketWatchers(id: number): Promise<{
    message: string
    watchers: {
      id: number
      name: string
      picture: string
    }[]
  }> {
    return apiService.get(`/support-tickets/${id}/watchers`).then((response) => response.data)
  }
}
